import { S, save } from '../core/state.js';
import { f, fT, esc } from '../core/utils.js';
import { toast, clMo } from '../ui/layout.js';
import { renderCF } from '../modules/cashflow.js';

function openBudgetSettings() {
  document.getElementById('bgTotalLimit').value = S.budgets?.monthlyLimit || '';
  // Build category rows from unique expense def types + custom categories
  const types = new Set();
  S.expenseDefs.forEach(d => { types.add(d.type); });
  types.add('Card'); types.add('Loan'); types.add('Other');
  const cats = S.budgets?.categories || {};
  const el = document.getElementById('bgCatRows');
  el.innerHTML = [...types].map(t => {
    const v = cats[t]?.limit || '';
    return `<div style="display:flex;gap:8px;align-items:center;margin-bottom:6px">
      <span style="width:80px;font-size:12px;font-weight:600">${esc(t)}</span>
      <input type="number" class="bgCatInput" data-cat="${t}" value="${v}" step="any" placeholder="No limit" style="flex:1">
      <span style="font-size:10px;color:var(--t3)">TRY/month</span></div>`;
  }).join('');
  document.getElementById('moBudget').classList.add('active');
}

function saveBudgetSettings() {
  S.budgets.monthlyLimit = parseFloat(document.getElementById('bgTotalLimit').value) || 0;
  document.querySelectorAll('.bgCatInput').forEach(inp => {
    const cat = inp.dataset.cat; const v = parseFloat(inp.value) || 0;
    if (v > 0) S.budgets.categories[cat] = { limit: v };
    else delete S.budgets.categories[cat];
  });
  save(); clMo('moBudget'); renderCF(); toast('Budget saved', 'a-b');
}

function renderBudget() {
  const card = document.getElementById('budgetCard');
  const bg = S.budgets;
  if (!bg || (!bg.monthlyLimit && !Object.keys(bg.categories || {}).length)) {
    if (card) card.style.display = 'none'; return;
  }
  card.style.display = 'block';

  // Determine which month to show
  const sel = document.getElementById('budgetMonth');
  const today = new Date(); const curK = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0');
  // Build month options from cashflow keys + current
  const allMonths = new Set(Object.keys(S.cashflow || {})); allMonths.add(curK);
  const sorted = [...allMonths].sort();
  const curVal = sel.value || curK;
  sel.innerHTML = sorted.map(m => `<option value="${m}" ${m === curVal ? 'selected' : ''}>${m}</option>`).join('');
  const k = sel.value || curK;
  document.getElementById('budgetMonthLabel').textContent = k;

  const mo = S.cashflow[k];
  // Calculate spending by category for this month
  const catSpend = {}; let totalSpend = 0;
  if (mo && mo.items) {
    mo.items.forEach(it => {
      const cat = it.type || 'Other';
      const amt = (it.confirmed && it.amount) ? it.amount : 0;
      catSpend[cat] = (catSpend[cat] || 0) + amt;
      totalSpend += amt;
    });
  }
  // Also include projected (unconfirmed with amounts) for projected view
  let totalProjected = 0; const catProjected = {};
  if (mo && mo.items) {
    mo.items.forEach(it => {
      const cat = it.type || 'Other'; const amt = it.amount || 0;
      catProjected[cat] = (catProjected[cat] || 0) + amt;
      totalProjected += amt;
    });
  }

  // Overall budget bar
  const ovEl = document.getElementById('budgetOverall');
  if (bg.monthlyLimit > 0) {
    const pct = bg.monthlyLimit > 0 ? (totalSpend / bg.monthlyLimit) * 100 : 0;
    const projPct = bg.monthlyLimit > 0 ? (totalProjected / bg.monthlyLimit) * 100 : 0;
    const col = pct >= 100 ? 'var(--red)' : pct >= 80 ? 'var(--yel)' : 'var(--grn)';
    const status = pct >= 100 ? 'OVER BUDGET' : pct >= 80 ? 'APPROACHING LIMIT' : 'ON TRACK';
    ovEl.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">
        <div style="font-size:12px;font-weight:600">Monthly Total: <span style="color:${col}">${fT(totalSpend)}</span> / ${fT(bg.monthlyLimit)}</div>
        <div style="font-size:11px;font-weight:700;color:${col}">${status} (${f(pct, 0)}%)</div>
      </div>
      <div class="gls-track" style="height:8px;border-radius:4px;overflow:hidden;position:relative">
        <div style="height:100%;width:${Math.min(projPct, 100)}%;background:rgba(245,158,11,.2);border-radius:4px;position:absolute;top:0;left:0"></div>
        <div style="height:100%;width:${Math.min(pct, 100)}%;background:${col};border-radius:4px;position:relative;z-index:1;transition:width .3s"></div>
      </div>
      <div style="font-size:10px;color:var(--t3);margin-top:4px;display:flex;justify-content:space-between">
        <span>Projected: ${fT(totalProjected)} (${f(projPct, 0)}%)</span>
        <span>Remaining: ${fT(Math.max(0, bg.monthlyLimit - totalSpend))}</span>
      </div>`;
  } else { ovEl.innerHTML = ''; }

  // Category traffic light cards
  const allCats = new Set([...Object.keys(bg.categories || {}), ...Object.keys(catSpend), ...Object.keys(catProjected)]);
  const cardsEl = document.getElementById('budgetCards');
  cardsEl.innerHTML = [...allCats].map(cat => {
    const limit = (bg.categories[cat]?.limit) || 0;
    const spent = catSpend[cat] || 0;
    const proj = catProjected[cat] || 0;
    const pct = limit > 0 ? (spent / limit) * 100 : 0;
    const projPct = limit > 0 ? (proj / limit) * 100 : 0;
    // Traffic light: <80% green, 80-100% yellow, >100% red
    const col = limit <= 0 ? 'var(--t3)' : pct >= 100 ? 'var(--red)' : pct >= 80 ? 'var(--yel)' : 'var(--grn)';
    const bgCol = limit <= 0 ? 'var(--bg2)' : pct >= 100 ? 'var(--red-bg)' : pct >= 80 ? 'var(--yel-bg)' : 'var(--grn-bg)';
    const icon = limit <= 0 ? '📊' : pct >= 100 ? '🔴' : pct >= 80 ? '🟡' : '🟢';
    const typeBadge = cat === 'Card' ? 'var(--pur)' : cat === 'Loan' ? 'var(--yel)' : 'var(--t2)';

    return `<div style="background:${bgCol};border:1px solid var(--bd);border-radius:var(--r);padding:12px;border-left:3px solid ${col}">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">
        <span style="font-size:12px;font-weight:700;color:${typeBadge}">${icon} ${esc(cat)}</span>
        ${limit > 0 ? `<span style="font-size:10px;color:var(--t3)">${f(pct, 0)}%</span>` : '<span style="font-size:10px;color:var(--t3)">No limit</span>'}
      </div>
      <div style="font-size:16px;font-weight:700;color:var(--t1)">${fT(spent)}</div>
      ${limit > 0 ? `<div style="font-size:10px;color:var(--t3)">of ${fT(limit)}</div>
      <div class="gls-track" style="height:4px;border-radius:2px;margin-top:6px;overflow:hidden;position:relative">
        <div style="height:100%;width:${Math.min(projPct, 100)}%;background:rgba(245,158,11,.15);border-radius:2px;position:absolute;top:0;left:0"></div>
        <div style="height:100%;width:${Math.min(pct, 100)}%;background:${col};border-radius:2px;position:relative;z-index:1"></div>
      </div>
      <div style="font-size:10px;color:var(--t3);margin-top:4px">Remaining: ${fT(Math.max(0, limit - spent))}</div>` : `<div style="font-size:10px;color:var(--t3);margin-top:4px">Projected: ${fT(proj)}</div>`}
    </div>`;
  }).join('');
}

export { openBudgetSettings, saveBudgetSettings, renderBudget };
